import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './ForgotPasswordScreen.module.css';
import { apiService } from '../../../utils/api';
import logoSecondLane from '../../../assets/SL-Top-Icon.png';

type ResetStep = 'email' | 'otp' | 'success';

// ForgotPasswordScreen
const ForgotPasswordScreen: React.FC = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<ResetStep>('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email) {
      setError('Please enter your email address');
      return;
    }

    try {
      setLoading(true);
      await apiService.requestPasswordReset({ email });
      setMessage('If your email is registered, a password reset code has been sent.');
      setCurrentStep('otp');
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send reset email';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!otp || !newPassword || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    try {
      setLoading(true);
      await apiService.confirmPasswordReset({
        email,
        otp,
        new_password: newPassword
      });
      setMessage('Password has been reset successfully.');
      setCurrentStep('success');
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to reset password';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  const handleBackToEmail = () => {
    setCurrentStep('email');
    setError(null);
    setMessage(null);
  };

  const renderEmailStep = () => (
    <form onSubmit={handleRequestReset} className={styles.form}>
      <div className={styles.formGroup}>
        <label htmlFor="email" className={styles.label}>Email</label>
        <input
          type="email"
          id="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Enter your email address"
          disabled={loading}
          className={styles.input}
          required
        />
      </div>
      
      <div className={styles.navigationButtons}>
        <button 
          type="button" 
          onClick={handleBackToLogin}
          className={styles.backButton}
          disabled={loading}
        >
          Back to Login
        </button>
        <button 
          type="submit" 
          className={`${styles.submitButton} ${!loading ? styles.activeButton : ''}`}
          disabled={loading}
        >
          {loading ? 'Sending...' : 'Send Reset Code'}
        </button>
      </div>
    </form>
  );

  const renderOtpStep = () => (
    <form onSubmit={handleConfirmReset} className={styles.form}>
      <div className={styles.formGroup}>
        <label htmlFor="otp" className={styles.label}>Reset Code</label>
        <input
          type="text"
          id="otp"
          value={otp}
          onChange={(e) => setOtp(e.target.value)}
          placeholder="Enter the 6-digit code from your email"
          disabled={loading}
          className={styles.input}
          maxLength={6}
          required
        />
      </div>
      
      <div className={styles.formGroup}>
        <label htmlFor="newPassword" className={styles.label}>New Password</label>
        <input
          type="password"
          id="newPassword"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          placeholder="Enter new password (min 8 characters)"
          disabled={loading}
          className={styles.input}
          minLength={8}
          required
        />
      </div>
      
      <div className={styles.formGroup}>
        <label htmlFor="confirmPassword" className={styles.label}>Confirm New Password</label>
        <input
          type="password"
          id="confirmPassword"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder="Confirm new password"
          disabled={loading}
          className={styles.input}
          required
        />
      </div>
      
      <div className={styles.navigationButtons}>
        <button 
          type="button" 
          onClick={handleBackToEmail}
          className={styles.backButton}
          disabled={loading}
        >
          Back
        </button>
        <button 
          type="submit" 
          className={`${styles.submitButton} ${!loading ? styles.activeButton : ''}`}
          disabled={loading}
        >
          {loading ? 'Resetting...' : 'Reset Password'}
        </button>
      </div>
    </form>
  );

  const renderSuccessStep = () => (
    <div className={styles.successContainer}>
      <div className={styles.successIcon}>✓</div>
      <h2 className={styles.successTitle}>Password Reset Successful!</h2>
      <p className={styles.successMessage}>
        Your password has been reset successfully. You can now log in with your new password.
      </p>
      <button 
        onClick={handleBackToLogin}
        className={`${styles.submitButton} ${styles.activeButton}`}
      >
        Back to Login
      </button>
    </div>
  );

  const getStepTitle = () => {
    switch (currentStep) {
      case 'email':
        return 'Forgot Password';
      case 'otp':
        return 'Reset Password';
      case 'success':
        return 'Success';
      default:
        return 'Forgot Password';
    }
  };

  const getStepDescription = () => {
    switch (currentStep) {
      case 'email':
        return 'Enter your email address to receive a password reset code';
      case 'otp':
        return 'Enter the code from your email and your new password';
      case 'success':
        return 'Your password has been reset successfully';
      default:
        return 'Enter your email address to receive a password reset code';
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.headerLogoContainer}>
        <img src={logoSecondLane} alt="SecondLane" className={styles.headerLogo} />
      </div>
      
      <div className={styles.formContainer}>
        <h1 className={styles.title}>{getStepTitle()}</h1>
        
        <p className={styles.description}>
          {getStepDescription()}
        </p>
        
        {message && (
          <div className={styles.message}>
            <span className={styles.messageIcon}>✓</span>
            {message}
          </div>
        )}
        
        {error && (
          <div className={styles.error}>
            <span className={styles.errorIcon}>!</span>
            {error}
          </div>
        )}
        
        {currentStep === 'email' && renderEmailStep()}
        {currentStep === 'otp' && renderOtpStep()}
        {currentStep === 'success' && renderSuccessStep()}
      </div>
    </div>
  );
};

export default ForgotPasswordScreen; 